import { useState } from "react";
import { useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { APPBAR_HEIGHT } from "consts/layout";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSChip from "views/system-design/DSChip";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSDrawer from "views/system-design/DSDrawer";
import DSTypography from "views/system-design/DSTypography";
import DSTooltip from "views/system-design/DSTooltip";
import { SentryIssue } from "../../../api/sentryIssues";
import CreateTicketModal from "../../Tickets/components/detail/CreateTicketModal";
import { TicketsProvider } from "../../Tickets/context/TicketsContext";
import {
  SENTRY_TICKETS_KEY,
  useSentryIssueDetails,
  useSentryIssueTickets,
  useSetSentryIssueStatus,
} from "../hooks/useSentryIssues";
import { buildSentryTicketDraft, openTicket } from "../sentryTicket";
import {
  DOMAIN_SOURCE_LABELS,
  domainColor,
  formatCount,
  levelColor,
  relativeTime,
} from "./domainStyles";

interface SentryIssueDrawerProps {
  /** The row that was clicked — shown immediately while details load. */
  issue: SentryIssue | null;
  onClose: () => void;
}

const MetaField = ({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) => (
  <DSBox sx={{ minWidth: 140 }}>
    <DSTypography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block" }}
    >
      {label}
    </DSTypography>
    <DSTypography variant="body2">{value ?? "—"}</DSTypography>
  </DSBox>
);

const SentryIssueDrawer = ({ issue, onClose }: SentryIssueDrawerProps) => {
  const { data, isLoading } = useSentryIssueDetails(issue?.id ?? null);
  const setStatus = useSetSentryIssueStatus();
  const [creatingTicket, setCreatingTicket] = useState(false);
  const queryClient = useQueryClient();
  const { spacing } = useTheme();
  // Start below the fixed app navbar, which is translucent and would otherwise
  // sit over the issue title and action buttons.
  const headerOffset = spacing(APPBAR_HEIGHT);

  // Prefer the freshly fetched issue, fall back to the row we already have so
  // the drawer paints instantly.
  const current = data?.issue ?? issue;
  const latestEvent = data?.latestEvent;
  const { data: linkedTickets } = useSentryIssueTickets(
    current?.permalink ?? null,
  );

  const act = (action: "resolve" | "ignore" | "unresolve") => {
    if (!current) return;
    setStatus.mutate({ issueId: current.id, action });
  };

  return (
    <DSDrawer
      anchor="right"
      open={issue !== null}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", md: 720 },
          maxWidth: "100vw",
          top: headerOffset,
          height: `calc(100% - ${headerOffset})`,
          p: 3,
        },
      }}
    >
      {!current ? null : (
        <DSBox>
          <DSBox sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <DSChip
              label={current.level}
              size="small"
              sx={{
                bgcolor: levelColor(current.level),
                color: "#fff",
                textTransform: "uppercase",
                fontSize: 10,
              }}
            />
            <DSTooltip title={DOMAIN_SOURCE_LABELS[current.domainSource]}>
              <DSChip
                label={current.domain}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: domainColor(current.domain),
                  color: domainColor(current.domain),
                }}
              />
            </DSTooltip>
            <DSTypography variant="caption" color="text.secondary">
              {current.shortId} · {current.projectSlug}
            </DSTypography>
            {current.status !== "unresolved" && (
              <DSChip label={current.status} size="small" color="success" />
            )}
          </DSBox>

          <DSTypography variant="h6" sx={{ mb: 0.5 }}>
            {current.title}
          </DSTypography>
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, fontFamily: "monospace" }}
          >
            {current.culprit ?? "—"}
          </DSTypography>

          <DSBox sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
            <DSButton
              variant="contained"
              size="small"
              onClick={() =>
                window.open(current.permalink, "_blank", "noreferrer")
              }
            >
              Open in Sentry
            </DSButton>
            <DSButton
              variant="outlined"
              size="small"
              // The draft quotes the latest event's release and stack, so wait
              // for it rather than prefill a thinner ticket.
              disabled={isLoading}
              onClick={() => setCreatingTicket(true)}
            >
              Create ticket
            </DSButton>
            {current.status === "unresolved" ? (
              <>
                <DSButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={setStatus.isPending}
                  onClick={() => act("resolve")}
                >
                  Resolve
                </DSButton>
                <DSButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={setStatus.isPending}
                  onClick={() => act("ignore")}
                >
                  Ignore
                </DSButton>
              </>
            ) : (
              <DSButton
                variant="outlined"
                color="secondary"
                size="small"
                disabled={setStatus.isPending}
                onClick={() => act("unresolve")}
              >
                Reopen
              </DSButton>
            )}
            {setStatus.isError && (
              <DSTypography variant="caption" color="error">
                {setStatus.error instanceof Error
                  ? setStatus.error.message
                  : "Failed to update in Sentry"}
              </DSTypography>
            )}
          </DSBox>

          {linkedTickets && linkedTickets.length > 0 && (
            <DSBox
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.5,
                mb: 2,
              }}
            >
              <DSTypography variant="caption" color="text.secondary">
                Tickets:
              </DSTypography>
              {linkedTickets.map((ticket) => (
                <DSTooltip key={ticket.id} title={ticket.title}>
                  <DSChip
                    size="small"
                    variant="outlined"
                    label={`${ticket.key} · ${ticket.status}`}
                    onClick={() => openTicket(ticket.id)}
                  />
                </DSTooltip>
              ))}
            </DSBox>
          )}

          <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
            <MetaField label="Project" value={current.projectSlug} />
            <MetaField
              label={`Events (window)`}
              value={formatCount(current.count)}
            />
            <MetaField
              label="Events (all time)"
              value={formatCount(current.lifetimeCount)}
            />
            <MetaField
              label="Users affected"
              value={formatCount(current.userCount)}
            />
            <MetaField
              label="First seen"
              value={relativeTime(current.firstSeen)}
            />
            <MetaField
              label="Last seen"
              value={relativeTime(current.lastSeen)}
            />
            <MetaField label="Assignee" value={current.assignedTo} />
            <MetaField label="Substatus" value={current.substatus} />
            <MetaField
              label="Handled"
              value={current.isUnhandled ? "unhandled" : "handled"}
            />
            <MetaField label="Release" value={latestEvent?.release ?? null} />
            <MetaField
              label="Environment"
              value={latestEvent?.environment ?? null}
            />
          </DSBox>

          {isLoading && (
            <DSBox sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <DSCircularProgress size={22} />
            </DSBox>
          )}

          {latestEvent && (
            <>
              {latestEvent.requestUrl && (
                <DSBox sx={{ mb: 2 }}>
                  <DSTypography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Request
                  </DSTypography>
                  <DSTypography
                    variant="body2"
                    sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                  >
                    {[latestEvent.requestMethod, latestEvent.requestUrl]
                      .filter(Boolean)
                      .join(" ")}
                  </DSTypography>
                </DSBox>
              )}

              <DSTypography variant="subtitle2" sx={{ mb: 0.5 }}>
                Latest event stack
              </DSTypography>
              <DSBox
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  maxHeight: 280,
                  overflowY: "auto",
                  // Frames scroll sideways rather than being clipped: the tail
                  // of the path and the line number are the useful part.
                  overflowX: "auto",
                }}
              >
                {latestEvent.frames.length === 0 ? (
                  <DSTypography variant="caption" color="text.secondary">
                    No stack frames on the latest event.
                  </DSTypography>
                ) : (
                  latestEvent.frames.map((frame, index) => (
                    <DSTypography
                      key={`${frame.filename}-${frame.lineNumber}-${index}`}
                      variant="caption"
                      sx={{
                        display: "block",
                        fontFamily: "monospace",
                        // Our own frames are what you act on; library frames
                        // are context.
                        opacity: frame.isInApp ? 1 : 0.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {frame.functionName ?? "<anonymous>"} —{" "}
                      {frame.filename ?? "unknown"}
                      {frame.lineNumber ? `:${frame.lineNumber}` : ""}
                    </DSTypography>
                  ))
                )}
              </DSBox>

              <DSTypography variant="subtitle2" sx={{ mb: 0.5 }}>
                Tags
              </DSTypography>
              <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {latestEvent.tags.map((tag) => (
                  <DSChip
                    key={tag.key}
                    size="small"
                    variant="outlined"
                    label={`${tag.key}: ${tag.value}`}
                    sx={{ fontSize: 11 }}
                  />
                ))}
              </DSBox>
            </>
          )}
        </DSBox>
      )}
      {creatingTicket && current && (
        <TicketsProvider>
          <CreateTicketModal
            initialValues={buildSentryTicketDraft(current, latestEvent)}
            onClose={() => setCreatingTicket(false)}
            onCreated={() =>
              queryClient.invalidateQueries({
                queryKey: SENTRY_TICKETS_KEY,
              })
            }
          />
        </TicketsProvider>
      )}
    </DSDrawer>
  );
};

export default SentryIssueDrawer;
